import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useLearningMode } from '../hooks/useLearningMode';

// Types
export interface BillingInfo {
  isActive?: boolean;
  status?: string;
  product?: {
    name?: string;
    id?: string;
    metadata?: {
      ai_call_limit?: number;
      [key: string]: any;
    };
  };
  [key: string]: any;
}

export interface AIUsageStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  currentCount: number;
  limit: number;
  totalUsage: number; // percentage
  breakdown?: Array<{
    endpoint: string;
    calls: number;
  }>;
  period?: {
    day: string;
    week: number;
    month: string;
  };
}

/**
 * Hook to get current plan from React Query cache
 * @param companyNumber - Optional company number for company context
 * @returns Current plan data or undefined
 */
export const useCurrentPlan = (companyNumber?: string | null): BillingInfo | undefined => {
  const queryClient = useQueryClient();
  
  // Include companyNumber in query key if provided
  const queryKey = companyNumber
    ? ['subscriptionPlan', companyNumber]
    : ['subscriptionPlan'];
  
  return queryClient.getQueryData(queryKey);
};

/**
 * Hook to check if user has an active paid subscription
 * @param companyNumber - Optional company number for company context
 * @returns boolean indicating if user has active paid subscription
 */
export const useHasActiveSubscription = (companyNumber?: string | null): boolean => {
  const currentPlan = useCurrentPlan(companyNumber);
  const isLearningMode = useLearningMode();
  
  return useMemo(() => {
    // If in learning mode, always return false (no paid features)
    if (isLearningMode) {
      return false;
    }
    
    if (!currentPlan) return false;

    // Check if plan is active
    const isActive = currentPlan.isActive !== false &&
      currentPlan.status !== 'cancelled' &&
      currentPlan.status !== 'expired' &&
      currentPlan.status !== 'inactive';

    // Check if it's not a free plan
    const planName = currentPlan?.product?.name?.toLowerCase() || '';
    const isFreePlan = planName.includes('free') ||
      planName.includes('starter') ||
      planName.includes('exempt') ||
      planName.includes('exempt_plan');

    return isActive && !isFreePlan;
  }, [currentPlan, isLearningMode]);
};

/**
 * Hook to check if user is on a free plan
 * @param companyNumber - Optional company number for company context
 * @returns boolean indicating if user is on free plan
 */
export const useIsFreePlan = (companyNumber?: string | null): boolean => {
  const currentPlan = useCurrentPlan(companyNumber);
  const hasActiveSubscription = useHasActiveSubscription(companyNumber);
  
  return useMemo(() => {
    if (!currentPlan) return true; // Default to free plan if no plan data

    const planName = currentPlan?.product?.name?.toLowerCase() || '';
    return planName.includes('free') ||
      planName.includes('starter') ||
      planName.includes('exempt') ||
      planName.includes('exempt_plan') ||
      !hasActiveSubscription;
  }, [currentPlan, hasActiveSubscription]);
};

/**
 * Hook to get AI usage statistics
 * @param options - Configuration options
 * @param options.enabled - Whether to fetch stats (defaults to true)
 * @param options.staleTime - Cache time in ms (defaults to 2 minutes)
 * @returns AI usage stats with loading state
 */
export const useAIUsageStats = (options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { enabled = true, staleTime = 2 * 60 * 1000 } = options || {};
  
  return useQuery({
    queryKey: ['aiUsageStats'],
    queryFn: async () => {
      const response = await apis.getAIUsageStats();
      return response?.data || response;
    },
    enabled,
    staleTime,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to get AI usage stats for free plan users only
 * Automatically fetches only when user is on free plan
 * @param companyNumber - Optional company number for company context
 * @returns AI usage stats with loading state
 */
export const useAIUsageStatsForFreePlan = (companyNumber?: string | null) => {
  const isFreePlan = useIsFreePlan(companyNumber);
  
  return useAIUsageStats({
    enabled: isFreePlan,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to get current plan's AI call limit
 * @param companyNumber - Optional company number for company context
 * @returns AI call limit or default value
 */
export const useAICallLimit = (companyNumber?: string | null): number => {
  const currentPlan = useCurrentPlan(companyNumber);
  const isFreePlan = useIsFreePlan(companyNumber);
  
  return useMemo(() => {
    const limit = currentPlan?.product?.metadata?.ai_call_limit;
    // Return limit if defined, otherwise default based on plan type
    if (limit !== undefined && limit !== null) {
      return limit;
    }
    // Default limits
    return isFreePlan ? 5 : 1000; // Free plan: 5, Paid plan: 1000
  }, [currentPlan, isFreePlan]);
};

/**
 * Hook to get comprehensive billing information
 * Combines plan info, subscription status, and AI usage
 * @param companyNumber - Optional company number for company context
 * @returns Comprehensive billing information
 */
export const useBillingInfo = (companyNumber?: string | null) => {
  const currentPlan = useCurrentPlan(companyNumber);
  const hasActiveSubscription = useHasActiveSubscription(companyNumber);
  const isFreePlan = useIsFreePlan(companyNumber);
  const aiCallLimit = useAICallLimit(companyNumber);
  const { data: aiUsageStats, isLoading: aiUsageLoading } = useAIUsageStatsForFreePlan(companyNumber);
  
  return useMemo(() => {
    const currentUsage = aiUsageStats?.currentCount || aiUsageStats?.thisMonth || 0;
    const usagePercentage = aiCallLimit > 0 
      ? Math.min(Math.round((currentUsage / aiCallLimit) * 100), 100) 
      : 0;
    
    return {
      currentPlan,
      hasActiveSubscription,
      isFreePlan,
      aiCallLimit,
      aiUsage: {
        current: currentUsage,
        limit: aiCallLimit,
        percentage: usagePercentage,
        stats: aiUsageStats,
        isLoading: aiUsageLoading,
      },
      planName: currentPlan?.product?.name || null,
      planId: currentPlan?.product?.id || null,
    };
  }, [
    currentPlan,
    hasActiveSubscription,
    isFreePlan,
    aiCallLimit,
    aiUsageStats,
    aiUsageLoading,
  ]);
};

/**
 * Utility function to check if a plan name indicates a free plan
 * @param planName - Plan name to check
 * @returns boolean indicating if plan is free
 */
export const isFreePlanName = (planName: string | null | undefined): boolean => {
  if (!planName) return true;
  const lowerName = planName.toLowerCase();
  return lowerName.includes('free') ||
    lowerName.includes('starter') ||
    lowerName.includes('exempt') ||
    lowerName.includes('exempt_plan');
};

/**
 * Utility function to check if plan status is active
 * @param plan - Plan object to check
 * @returns boolean indicating if plan is active
 */
export const isPlanActive = (plan: BillingInfo | null | undefined): boolean => {
  if (!plan) return false;
  return plan.isActive !== false &&
    plan.status !== 'cancelled' &&
    plan.status !== 'expired' &&
    plan.status !== 'inactive';
};

/**
 * Utility function to get company number from Redux state
 * @returns Company number or null
 */
export const useCompanyNumber = (): string | null => {
  const company = useSelector((state: DefaultRootState) => 
    state.promiseData.data?.companyData || null
  );
  return company?._id || company?.id || null;
};

/**
 * Hook that automatically uses company context
 * Uses companyNumber from Redux if available, falls back to user context
 * @returns Billing info with automatic company context
 */
export const useBillingInfoWithContext = () => {
  const companyNumber = useCompanyNumber();
  return useBillingInfo(companyNumber);
};

/**
 * Hook to check if user has active subscription with automatic company context
 * @returns boolean indicating if user has active paid subscription
 */
export const useHasActiveSubscriptionWithContext = (): boolean => {
  const companyNumber = useCompanyNumber();
  return useHasActiveSubscription(companyNumber);
};

/**
 * Hook to check if user is on free plan with automatic company context
 * @returns boolean indicating if user is on free plan
 */
export const useIsFreePlanWithContext = (): boolean => {
  const companyNumber = useCompanyNumber();
  return useIsFreePlan(companyNumber);
};

